import { apiClient } from "./apiClient";
import type { SupportEligibility, SupportEligibilityResponse } from "../types";
import { camelCaseKeysDeep } from "./camelCaseKeys";
import { logError } from "@/utils/logError";
import { DEFAULT_SUPPORT_ELIGIBILITY } from "@zapier/copilot-ui";

// Re-export so existing importers of DEFAULT_SUPPORT_ELIGIBILITY from this
// module keep working; the canonical default now lives in @zapier/copilot-ui.
export { DEFAULT_SUPPORT_ELIGIBILITY };

/**
 * Fetches support eligibility from the API and transforms the response to camelCase.
 * This shared utility is used by both the React hook (client-side) and server-side route handlers.
 *
 * Logic:
 * - Calls the API to get support eligibility data (snake_case)
 * - Converts all keys from snake_case to camelCase
 * - Returns a default eligibility object if the API call fails
 *
 * @param options - Optional configuration
 * @param options.baseUrl - Base URL for server-side requests (defaults to relative path for client-side)
 * @param options.cookies - Cookie string to forward for server-side requests
 * @returns Promise resolving to SupportEligibility (camelCase)
 */
export async function fetchSupportEligibility(options?: {
  baseUrl?: string;
  cookies?: string;
}): Promise<SupportEligibility> {
  // Server-side path: use fetch with forwarded cookies
  if (options?.baseUrl && options?.cookies !== undefined) {
    const url = `${options.baseUrl}/api/supportfrontend/v1/proxy/support-eligibility`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          cookie: options.cookies,
          "Content-Type": "application/json",
        },
        cache: "no-store",
      });

      if (!response.ok) {
        console.warn(
          `Failed to fetch support eligibility (server-side): ${response.status}`,
        );
        return DEFAULT_SUPPORT_ELIGIBILITY;
      }

      const data: SupportEligibilityResponse = await response.json();

      // Convert snake_case to camelCase
      return camelCaseKeysDeep(data) as SupportEligibility;
    } catch (error) {
      logError("Failed to fetch support eligibility (server-side)", error, {
        app: "get-help",
        feature: "support-eligibility",
      });
      return DEFAULT_SUPPORT_ELIGIBILITY;
    }
  }

  // Client-side path: use apiClient
  try {
    const response = await apiClient.getSupportEligibility();

    if (!response) {
      return DEFAULT_SUPPORT_ELIGIBILITY;
    }

    // Convert snake_case to camelCase
    return camelCaseKeysDeep(response) as SupportEligibility;
  } catch (error) {
    logError("Failed to fetch support eligibility (client-side)", error, {
      app: "get-help",
      feature: "support-eligibility",
    });
    return DEFAULT_SUPPORT_ELIGIBILITY;
  }
}
